import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CreateFontGQL } from 'ZalamoLibrary/src/lib/ui/fonts-manager/graphql';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import { combineLatest, first, map, switchMap, tap } from 'rxjs';

import { UploadContext } from 'Upload/upload/graphql';
import cuid from 'cuid';
import { UploadService } from 'ZalamoLibrary/src/lib/upload/upload.service';
import { FontsManagerService } from '../fonts-manager.service';
import { localFontList } from '@ui/fonts-manager/localFontList';
import { FontLoaderService } from '@ui/fonts-display/font-loader.service';
import { firstValueFrom } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';

@Component({
  selector: 'lib-font-list',
  templateUrl: './font-list.component.html',
  styleUrls: ['./font-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FontListComponent implements OnInit {
  fonts$ = this.fontsManagerService.getFonts().pipe(
    tap(async (fonts) => {
      const organizationId = await firstValueFrom(
        this.organizationService.getCurrentOrganization(),
      );
      Promise.all(
        fonts.map(({ fontId, fileName }) =>
          this.fontLoaderService.loadFont(fontId, fileName, organizationId),
        ),
      );
    }),
  );

  @ViewChild('upload', { static: true })
  uploadInput: ElementRef<HTMLInputElement>;
  @Output() picked = new EventEmitter<{ fontId: string; fileName: string }>();
  header: any;
  localFontList = localFontList;
  constructor(
    private fontsManagerService: FontsManagerService,
    private organizationService: OrganizationService,
    private uploadService: UploadService,
    private createFontGQL: CreateFontGQL,
    private fontLoaderService: FontLoaderService,
  ) {}

  ngOnInit(): void {
    this.localFontList.forEach(({ fontId, fileName }) =>
      firstValueFrom(
        this.fontLoaderService.loadFont(fontId, fileName, 'zalamo'),
      ),
    );
  }

  pickedFile() {
    combineLatest(
      Array.from(this.uploadInput.nativeElement.files).map((file) =>
        this.organizationService
          .getCurrentOrganization()
          .pipe(
            switchMap((organizationId) =>
              this.uploadService
                .uploadSingleFile(
                  file,
                  UploadContext.DecorationFile,
                  'fonts',
                  organizationId,
                )
                .pipe(map((fileName) => ({ fileName, file }))),
            ),
          ),
      ),
    )
      .pipe(
        switchMap((uploads) =>
          combineLatest(
            uploads.map(({ fileName, file }) =>
              this.organizationService.getCurrentOrganization().pipe(
                switchMap((organizationId) =>
                  this.createFontGQL.mutate({
                    variables: {
                      input: {
                        name: file.name.split('.').slice(0, -1).join('.'),
                        fileName,
                        fontId: cuid(),
                        organizationId,
                      },
                    },
                  }),
                ),
              ),
            ),
          ),
        ),
      )
      .subscribe(async (uploads) => {
        await this.fontsManagerService.getFonts(true).pipe(first()).toPromise();
      });
  }
}
